import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  AdminPage,
  Button,
  ErrorState,
  Panel,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { useEmployees } from '@/features/employees/api';
import { useLeaderboard } from '../api';
import { useGamesMutations } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * מתן נקודות ידני (PRD 5.8.1).
 *
 * הרישום כולל תמיד מי נתן ולמה. ה RLS דורש ש granted_by יהיה העובד
 * המחובר, ולכן אי אפשר לתת נקודות בשם מישהו אחר, וגם מי שנותן
 * לעצמו משאיר חתימה.
 */
export function GrantPointsPage() {
  const canGrant = usePerm('points.grant');
  const { me } = useMe();
  const { data: employees } = useEmployees();
  const board = useLeaderboard(null);
  const mutations = useGamesMutations();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!canGrant) return <Navigate to="/admin" replace />;

  return (
    <AdminPage title="מתן נקודות" subtitle="נקודות ידניות, עם סיבה">
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="m-0 text-body-sm text-good">
          {done}
        </p>
      )}

      <Panel title="נקודות לעובד/ת">
        <form
          className="grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            const formElement = event.currentTarget;
            const form = new FormData(formElement);
            const employeeId = String(form.get('employee') ?? '');
            const points = Number(form.get('points') ?? 0);
            const reason = String(form.get('reason') ?? '').trim();
            if (!employeeId || !points || !reason || !me?.employeeId) return;

            setError(null);
            setDone(null);
            mutations.grantPoints
              .mutateAsync({ employeeId, points, reason, grantedBy: me.employeeId })
              .then(() => {
                const name = employees?.find((e) => e.id === employeeId);
                setDone(
                  `נרשמו ${points} נקודות ל${name ? `${name.first_name} ${name.last_name}` : 'עובד/ת'}.`,
                );
                formElement.reset();
              })
              .catch((caught) => setError((caught as Error).message));
          }}
        >
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
            <div className="grid gap-1">
              <label htmlFor="employee" className="text-body-sm text-muted">
                למי
              </label>
              <select id="employee" name="employee" className={FIELD} required>
                <option value="">בחירת עובד/ת</option>
                {employees
                  ?.filter((employee) => employee.is_active)
                  .map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.first_name} {employee.last_name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="grid gap-1">
              <label htmlFor="points" className="text-body-sm text-muted">
                כמה נקודות
              </label>
              <input id="points" name="points" type="number" className={FIELD} required />
              <p className="m-0 text-label text-muted">מספר שלילי מוריד נקודות.</p>
            </div>
          </div>

          <div className="grid gap-1">
            <label htmlFor="reason" className="text-body-sm text-muted">
              הסיבה, כפי שהעובד/ת תראה אותה
            </label>
            <input id="reason" name="reason" className={FIELD} required />
          </div>

          <div>
            <Button variant="primary" type="submit" disabled={mutations.grantPoints.isPending}>
              <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
              רישום הנקודות
            </Button>
          </div>
        </form>
      </Panel>

      <Panel title="המובילים החודש">
        {board.isPending && <Skeleton className="h-32" />}
        {board.isError && <ErrorState onRetry={() => void board.refetch()} />}
        {board.data && board.data.length === 0 && (
          <p className="m-0 text-body-sm text-muted">עוד לא נצברו נקודות החודש.</p>
        )}
        {board.data && board.data.length > 0 && (
          <Table>
            <thead>
              <Tr>
                <Th>מקום</Th>
                <Th>עובד/ת</Th>
                <Th>נקודות</Th>
              </Tr>
            </thead>
            <tbody>
              {board.data.map((row, i) => (
                <Tr key={row.employee_id}>
                  <Td className="tabular">{i + 1}</Td>
                  <Td>{row.full_name}</Td>
                  <Td className="tabular">{row.points}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
    </AdminPage>
  );
}
